import { BaseMeiucaService } from './MeiucaService'
import { env } from '@/lib/env'
import type { ReservationItem } from './reservationService'

export interface VenueModalitiesResponse {
  data: string[]
}

export interface CourtAvailabilityParams {
  date: string // ISO date string
  modality?: string
}

export interface CourtAvailability {
  id: string
  name: string
  pricePerHour?: number
  color?: string
}

export interface CourtPricing {
  courtId: string
  /** Preço de tabela — é o que a criação envia; campanha nunca altera. */
  priceCents: number
  /** Preço final com a campanha vencedora do slot; ausente quando nenhuma se aplica. */
  campaignPriceCents?: number | null
}

export interface AvailableSlot {
  startMinutes: number // Minutes since midnight
  endMinutes: number // Minutes since midnight
  courtPricings: CourtPricing[]
}

export interface AvailableSchedule {
  date: string
  availables: AvailableSlot[]
}

export interface CourtAvailabilityResponse {
  data: {
    availableSchedules: AvailableSchedule[]
    courts: CourtAvailability[]
  }
}

// --- Checkout options (available payment methods) ---

export type CheckoutPaymentMethodId = 'pix_online' | 'manual'

export interface CheckoutPaymentMethod {
  id: CheckoutPaymentMethodId
  title: string
}

/** Preço por slot (na ORDEM do request) — o "de → por" quando há campanha. */
export interface CheckoutItemPricing {
  courtId: string
  startsAt: string
  endsAt: string
  /** Preço de tabela do slot (o "de"). */
  priceCents: number
  discountCents: number
  /** Quanto o slot efetivamente custa (o "por"). */
  finalPriceCents: number
}

export interface CheckoutOptionsResponse {
  paymentMethods: CheckoutPaymentMethod[]
  /** Campos aditivos de campanha — opcionais para compatibilidade com backend antigo. */
  items?: CheckoutItemPricing[]
  /** Subtotal de tabela. */
  totalAmountCents?: number
  /** Desconto de campanha da reserva inteira (0 = nenhum). */
  discountAmountCents?: number
  /** O que o cliente efetivamente paga. */
  finalAmountCents?: number
}

class VenueBookingService extends BaseMeiucaService {
  /**
   * Get available modalities for a venue
   * @param venueId - The venue ID
   * @param courtIds - Optional array of court IDs to filter modalities
   */
  async getVenueModalities(venueId: string, courtIds?: string[]): Promise<string[]> {
    const params = new URLSearchParams()
    if (courtIds && courtIds.length > 0) {
      courtIds.forEach(courtId => params.append('courtIds[]', courtId))
    }

    const queryString = params.toString()
    const url = `/venues/${venueId}/modalities${queryString ? `?${queryString}` : ''}`

    const response = await this.get<VenueModalitiesResponse>(url)
    return response.data.data
  }

  /**
   * Get court availability for a venue
   * @param venueId - The venue ID
   * @param params - Query parameters (date, modality)
   */
  async getCourtAvailability(
    venueId: string,
    params: CourtAvailabilityParams
  ): Promise<{ availableSchedules: AvailableSchedule[]; courts: CourtAvailability[] }> {
    const queryParams = new URLSearchParams({
      date: params.date,
    })

    if (params.modality) {
      queryParams.append('modality', params.modality)
    }

    const response = await this.get<CourtAvailabilityResponse>(
      `/venues/${venueId}/courts/availability?${queryParams.toString()}`
    )
    return response.data.data
  }

  /**
   * Fetch the payment methods available for a prospective reservation
   * (Pix / pay-at-venue), mirroring the mobile checkout step.
   */
  async getCheckoutOptions(
    venueId: string,
    body: { priceCents: number; items: ReservationItem[] }
  ): Promise<CheckoutOptionsResponse> {
    const response = await this.post<{ data: CheckoutOptionsResponse }>(
      `/venues/${encodeURIComponent(venueId)}/reservations/checkout-options`,
      body
    )
    return response.data.data
  }
}

export const venueBookingService = new VenueBookingService({
  baseURL: env.API_URL,
  refreshEndpoint: '/v1/auth/refresh',
  cookiePrefix: '',
  debug: env.IS_DEVELOPMENT,
})
